import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  message
} from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CurrencyInputCustom from '../../../components/CurrencyInputCustom';
import ResponsiveContainer from '../../../components/ResponsiveContainer';
import TableAndFilters from '../../../components/TableAndFilters';
import { currencyToNumber, errorHandler } from '../../../utils';
import { instanceRef } from '../../../utils/axios';
import { formatoMonedaGenerico } from '../../../utils/converters';
import { fieldTypes, filterConditions } from '../../../utils/filter-utils';

const { Option } = Select;

function AnioSubmodelos() {
  const tableRef = useRef();
  const [form] = Form.useForm();
  const [formFiltro] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [submodelos, setSubmodelos] = useState([]);
  const [aniosList, setAniosList] = useState([]); // lista de años para selectores del form. Ej: 2026...1990
  const aniosSubmodelosList = []; // lista de años del submodelo seleccionado.
  const [monedaSeleccionada, setMonedaSeleccionada] = useState();
  const [initialValues, setInitialValues] = useState();

  const [editData, setEditData] = useState(null);
  const [recordToEdit, setRecordToEdit] = useState();

  const [defaultFilters, setDefaultFilters] = useState({
    id_submodelo: {
      value: -1, // Valor inicial ara obtener un listado vacio, ya que es necesario mostrar la tabla vacia porque existen botones "Agregar" que se deben mostrar si o si.
      filterType: fieldTypes.NUMERIC,
      filterCondition: filterConditions.EQUALS
    }
  });

  const columnas = [
    {
      title: 'Año',
      dataIndex: ['codigo'],
      filterType: fieldTypes.NUMERIC,
      render: (codigo) => {
        aniosSubmodelosList.push(codigo);
        return codigo;
      }
    },
    {
      title: 'Monto',
      dataIndex: ['monto'],
      filterType: fieldTypes.NUMERIC,
      render: (monto) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          {formatoMonedaGenerico(monedaSeleccionada, monto)}
        </div>
      )
    },
    {
      title: 'Monto Máximo',
      dataIndex: ['monto_max'],
      filterType: fieldTypes.NUMERIC,
      render: (montoMax) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          {formatoMonedaGenerico(monedaSeleccionada, montoMax)}
        </div>
      )
    },
    {
      title: 'Monto Mínimo',
      dataIndex: ['monto_min'],
      filterType: fieldTypes.NUMERIC,
      render: (montoMin) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          {formatoMonedaGenerico(monedaSeleccionada, montoMin)}
        </div>
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (record) => {
        return (
          <Space>
            <Tooltip title="Editar">
              <Button
                type="text"
                icon={<EditOutlined />}
                size="middle"
                style={{ color: 'rgba(0,0,0,.45)' }}
                onClick={() => onClickEditar(record)}
              />
            </Tooltip>
            <Tooltip title="Eliminar">
              <DeleteOutlined style={{ color: 'rgba(0,0,0,.45)' }} disabled />
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  const handleClose = () => {
    setEditData(false);
    // form.resetFields();
    setIsModalOpen(false);
  };

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      const validacionAnios = !editData ? validarAnios() : true;
      if (validacionAnios) {
        const data = {
          id: recordToEdit && recordToEdit.id,
          idSubmodelo: values.id_submodelo,
          monto: values.monto,
          montoMin: values.monto_min,
          montoMax: values.monto_max,
          anioMayor: values.anio_mayor,
          anioMenor: values.anio_menor
        };
        const url = editData ? '/aniosubmodelo/update' : '/aniosubmodelo/add';

        const response = await instanceRef.instance.post(url, data);
        if (response.data?.data) {
          if (!editData) {
            message.success('Años del SubModelo registrados correctamente');
          } else {
            message.success('Años del SubModelo actualizado correctamente');
          }

          tableRef.current.reload();
          handleClose();
        }
      } else {
        message.warning(
          'El Submodelo ya tiene asignado uno o más Años dentro del rango seleccionado.'
        );
      }

      setEditData(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      message.error('Los años del SubModelo no pudieron ser registrados');
      handleClose();
    }
    // cerrar modal
  };

  const onClickEditar = (record) => {
    form.setFieldsValue({
      codigo: record.codigo,
      monto: record.monto,
      monto_min: record.monto_min,
      monto_max: record.monto_max,
      anio_mayor: record.codigo,
      anio_menor: record.codigo
    });
    setEditData(true);
    setRecordToEdit(record);
    setIsModalOpen(true);
  };

  const onChangeSubmodelo = (value) => {
    form.setFieldValue('id_submodelo', value);
    setDefaultFilters({
      id_submodelo: {
        value: Number(value),
        filterType: fieldTypes.NUMERIC,
        filterCondition: filterConditions.EQUALS
      }
    });
    setTimeout(() => {
      tableRef.current.reload();
    }, 500);
    setInitialValues({
      ...initialValues,
      id_submodelo: value
    });
  };

  const onChangeModelo = (value) => {
    form.setFieldValue('id_modelo', value);

    formFiltro.setFieldValue('id_submodelo', null);

    getSubmodelos(value);

    setInitialValues({
      ...initialValues,
      id_modelo: value,
      id_submodelo: null
    });
  };

  const onChangeMarca = (value) => {
    getModelos(value);

    form.setFieldValue('id_marca', value);
    formFiltro.setFieldValue('id_modelo', null);
    formFiltro.setFieldValue('id_submodelo', null);

    setInitialValues({
      ...initialValues,
      id_marca: value,
      id_modelo: null,
      id_submodelo: null
    });
  };

  const onBlurMonto = (montoString) => {
    const montoANumber = currencyToNumber(montoString, monedaSeleccionada);
    form.setFieldValue('monto', montoANumber);
    form.validateFields(['monto']);

    calcularMontoMinMax(montoANumber);
  };

  const calcularMontoMinMax = (montoANumber) => {
    // Se calcula el 10% menos y más en base al monto
    const monto20Porc = Math.ceil(montoANumber * 0.2);

    const min = montoANumber - monto20Porc;
    const max = montoANumber + monto20Porc;

    form.setFieldValue('monto_min', min);
    form.setFieldValue('monto_max', max);
  };

  const existeCoincidencia = (arr1, arr2) => {
    const set1 = new Set(arr1);
    return arr2.some((num) => set1.has(num));
  };
  const validarAnios = () => {
    const anioMayor = form.getFieldValue('anio_mayor');
    const anioMenor = form.getFieldValue('anio_menor');
    const anioRangoIngresadoList = [];

    for (let anio = anioMayor; anio >= anioMenor; anio -= 1) {
      anioRangoIngresadoList.push(anio);
    }

    const coincideAnios = existeCoincidencia(
      anioRangoIngresadoList,
      aniosSubmodelosList
    );
    if (coincideAnios) {
      return false;
    }
    return true;
  };

  const getMonedas = async () => {
    try {
      const monedasResponse = await instanceRef.instance.get(`/moneda`);
      if (monedasResponse.data?.data) {
        const monedas = monedasResponse.data?.data;
        const monedaGs = monedas.find((item) => item.codigo === 'gs');
        setMonedaSeleccionada(monedaGs);
      }
    } catch (error) {
      errorHandler(error, 'No se pudo obtener monedas');
      setLoading(false);
    }
  };

  const generarListaAnios = (anioInicial) => {
    const anioActual = new Date().getFullYear();
    const anioSiguiente = anioActual + 1;
    const listaAnios = [];

    for (let anio = anioSiguiente; anio >= anioInicial; anio -= 1) {
      listaAnios.push(anio);
    }

    setAniosList(listaAnios);
  };

  const getSubmodelos = useCallback(async (idModelo) => {
    try {
      const response = await instanceRef.instance.get(
        `data/submodelos/modelo?modelo=${idModelo}`
      );
      const orderedSubmodelos = response.data.data
        ? response.data.data.sort(function test(a, b) {
            if (a.descripcion < b.descripcion) {
              return -1;
            }
            if (a.descripcion > b.descripcion) {
              return 1;
            }
            return 0;
          })
        : [];
      setSubmodelos(orderedSubmodelos);
    } catch (error) {
      // error
    }
  }, []);

  const getModelos = useCallback(async (idMarca) => {
    try {
      const response = await instanceRef.instance.get(
        `data/modelos?marca=${idMarca}`
      );
      const orderedModelos = response.data.data
        ? response.data.data.sort(function test(a, b) {
            if (a.descripcion < b.descripcion) {
              return -1;
            }
            if (a.descripcion > b.descripcion) {
              return 1;
            }
            return 0;
          })
        : [];
      setModelos(orderedModelos);
    } catch (error) {
      // error
    }
  }, []);

  const getMarcas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await instanceRef.instance.get(`data/marcas`);
      const orderedMarcas = response.data.data.sort(function test(a, b) {
        if (a.descripcion < b.descripcion) {
          return -1;
        }
        if (a.descripcion > b.descripcion) {
          return 1;
        }
        return 0;
      });
      setMarcas(orderedMarcas);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // error
    }
  }, []);

  useEffect(() => {
    getMarcas();
    generarListaAnios(1990);
    getMonedas();
  }, [getMarcas]);

  return (
    <ResponsiveContainer size="large" style={{ paddingTop: 0 }}>
      <Card
        style={{ marginBottom: 5, textAlign: 'left' }}
        title="Años de Submodelo (seleccionar datos para gestionar)"
      >
        <Form
          form={formFiltro}
          scrollToFirstError
          layout="horizontal"
          name="historico"
          autoComplete="off"
        >
          <Row justify="start">
            <Col xs={24} md={12}>
              <Form.Item label="Marca" name="id_marca">
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  onChange={(value) => {
                    onChangeMarca(value);
                  }}
                >
                  {marcas.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.descripcion}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Modelo" name="id_modelo">
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  onChange={(value) => {
                    onChangeModelo(value);
                  }}
                >
                  {modelos.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.descripcion}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Submodelo" name="id_submodelo">
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  onChange={(value) => {
                    onChangeSubmodelo(value);
                  }}
                >
                  {submodelos.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.descripcion}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
      {defaultFilters && (
        <TableAndFilters
          ref={tableRef}
          withGeneralFilter
          url="/aniosubmodelo"
          defaultFilters={defaultFilters}
          title="Años del Submodelo"
          columns={columnas}
          showTable
          hasExportar
          extra={[
            <Tooltip
              title={`${
                !initialValues?.id_submodelo
                  ? 'Seleccionar los datos para gestionar Año del Submodelo'
                  : ''
              }`}
            >
              <Button
                key="agregarModelo"
                type="dashed"
                shape="round"
                icon={<PlusCircleOutlined />}
                size="middle"
                onClick={() => {
                  form.setFieldValue('monto', 0);
                  form.setFieldValue('monto_min', null);
                  form.setFieldValue('monto_max', null);
                  form.setFieldValue('anio_mayor', null);
                  form.setFieldValue('anio_menor', null);
                  setIsModalOpen(true);
                }}
                disabled={!initialValues?.id_submodelo}
              >
                Agregar Año/s Submodelo
              </Button>
            </Tooltip>
          ]}
        />
      )}

      {/* <ModalCrearModelo /> */}
      <Modal
        title={!editData ? 'Agregar Año Submodelo' : 'Editar Año Submodelo'}
        open={isModalOpen}
        onCancel={handleClose}
        width={1000}
        footer={[
          <Button key="back" onClick={() => handleClose()}>
            Cancelar
          </Button>,
          <Button
            type="primary"
            onClick={() => {
              form.validateFields().then((values) => {
                handleFormSubmit(values);
              });
            }}
          >
            Guardar
          </Button>
        ]}
      >
        <Spin spinning={loading} size="large" tip="Cargando...">
          <br />
          <Form
            form={form}
            defaultValue={{ ...initialValues }}
            layout="vertical"
          >
            <Row gutter={[16, 0]} style={{ textAlign: 'start' }}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Marca"
                  name="id_marca"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Seleccionar Marca"
                    onChange={(value) => {
                      getModelos(value);
                    }}
                    disabled
                  >
                    {marcas.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Modelo"
                  name="id_modelo"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Seleccionar Modelo"
                    onChange={(value) => {
                      getSubmodelos(value);
                    }}
                    disabled
                  >
                    {modelos.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="SubModelo"
                  name="id_submodelo"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Seleccionar Submodelo"
                    disabled
                  >
                    {submodelos.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="monto"
                  label="Monto"
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: 'Debe indicar el monto a asegurar'
                  //   },
                  //   {
                  //     validator: validarLimiteMonto
                  //   }
                  // ]}
                >
                  <CurrencyInputCustom
                    moneda={monedaSeleccionada}
                    // onChange={(valueString) => onBlurMonto(valueString)}
                    onBlur={(e) => onBlurMonto(e.target?.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="monto_max"
                  label="Monto Máximo calculado (+20%)"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <CurrencyInputCustom moneda={monedaSeleccionada} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="monto_min"
                  label="Monto Mínimo calculado (-20%)"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <CurrencyInputCustom moneda={monedaSeleccionada} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Año Mayor (más reciente)"
                  name="anio_mayor"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Seleccionar Año"
                    disabled={editData}
                  >
                    {aniosList.map((item) => (
                      <Option key={item} value={item}>
                        {item}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="anio_menor"
                  label="Año Menor (más antigüo)"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Seleccionar Año"
                    disabled={editData}
                  >
                    {aniosList.map((item) => (
                      <Option key={item} value={item}>
                        {item}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
    </ResponsiveContainer>
  );
}

export default AnioSubmodelos;
